import { Order } from '~/lib/types/order';
import { Product, ProductItem } from './product-types';
import {
  BasketItem,
  BasketQuote,
  QuoteAddress,
  QuoteDates,
  QuoteExtras,
  QuoteParcels,
  Service
} from '~/lib/types/basket-types';
import { BasketSections } from '~/lib/types/enums';
import { getBestQuoteDate, isQuoteDropOff } from '~/lib/quotes';
import { Label } from '~/lib/types/extra-types';

export function getProductsFromOrder(order: Order): Product {
  return {
    id: order.id,
    currency: { code: order.currency.currencyCode, symbol: order.currency.currencySymbol },
    items: order.items.map((item) => ({
      id: item.id.toString(),
      booked: item.booked,
      protectionAmount: item.protectionAmount,
      source: item.source,

      labelStatusHash: item.labelStatusHash,

      service: item.service,
      origin: item.origin,
      destination: item.destination,
      dates: item.dates,
      price: item.price,
      extras: item.extras,
      parcels: item.parcels,

      importedItems: item.importedItems
    }))
  };
}

export function getProductsFromBasket(
  basketId: string,
  items: {
    name: string;
    item: BasketItem;
  }[]
): Product {
  if (items.length === 0) {
    return {
      id: basketId,
      currency: { code: '', symbol: '' },
      items: []
    };
  }

  const firstQuote = items[0].item.sections[BasketSections.QUOTE].payload as BasketQuote;
  const curr = firstQuote.currency;

  const productItems: ProductItem[] = items.map(({ name, item }) => {
    const origin = item.sections[BasketSections.ORIGIN].payload as QuoteAddress;
    const service = item.sections[BasketSections.SERVICE].payload as Service;
    const destination = item.sections[BasketSections.DESTINATION].payload as QuoteAddress;
    const parcels = item.sections[BasketSections.PARCELS].payload as QuoteParcels;
    const quote = item.sections[BasketSections.QUOTE].payload as BasketQuote;
    const extras = item.sections[BasketSections.EXTRAS]?.payload as QuoteExtras;
    const dates = (item.sections[BasketSections.DATES]?.payload as QuoteDates) || { dates: [] };

    const quoteDate = getBestQuoteDate(quote, dates);

    return {
      id: name,
      currency: { code: quote.currency.code, symbol: quote.currency.symbol },
      service: {
        name: service.name ?? quote.service.name,
        isDropOffService: isQuoteDropOff(quote),
        isShopDelivery: !!destination.shop,
        slug: service.slug ?? quote.service.slug
      },
      origin: {
        ...origin,
        contactName: origin.contactname || '',
        country: { key: origin.country, name: origin.country }
      },
      destination: {
        ...destination,
        contactName: destination.contactname || '',
        country: { key: destination.country, name: destination.country }
      },
      dates: {
        collectionDate: quoteDate?.collection?.date ?? dates.dates.find((x) => x.type === 'collection')?.date ?? null,
        estimatedDeliveryDate:
          quoteDate?.delivery?.dateMin ?? dates.dates.find((x) => x.type === 'delivery')?.date ?? null,
        collectionDateCutoff: quoteDate?.cutoff ?? null,
        deliveryDateMin: quoteDate?.delivery?.dateMin ?? null,
        deliveryDateMax: quoteDate?.delivery?.dateMax ?? null
      },
      extras: extras.extras.map((extra) => ({
        type: extra.key,
        amount: quote.availableExtras.find((x) => x.key == extra.key)?.price.gross
      })),
      parcels: parcels.parcels.map((parcel) => ({
        contents: parcel.contentSummary,
        height: parcel.height,
        length: parcel.length,
        width: parcel.width,
        weight: parcel.weight
      })),
      price: {
        subTotal: quote.price.net || 0,
        total: quote.price.gross || 0,
        discount: quote.price.discount
      },

      quoteOffersPrintInStore: quote.availableExtras?.some((x) => x.key === Label.STORE)
    } as ProductItem;
  });

  return {
    id: basketId,
    currency: { code: curr.code, symbol: curr.symbol },
    items: productItems
  };
}
